import React, { Component } from 'react';
import Loader from '../utils/Loadersmall';
import { storefrontApi } from '../../api/Api';
import '../HomeVideoBanner.css';

const CONTENT_KEYS = [
    'html',
    'content',
    'description',
    'body',
    'text',
    'rich_text',
    'richText',
    'editor_content',
    'editorContent',
    'page_content',
    'pageContent',
];

const MEDIA_URL_KEYS = [
    'image_url',
    'media_url',
    'file_url',
    'video_url',
    'promotion_url',
    'url',
];

const parseSettings = (settings) => {
    if (typeof settings !== 'string') {
        return settings;
    }

    try {
        return JSON.parse(settings);
    } catch (error) {
        return settings;
    }
};

const isHtmlString = (value) => /<\/?[a-z][\s\S]*>/i.test(value);

const isMediaUrl = (value) => /\.(avif|gif|jpe?g|png|svg|webp)(\?.*)?$/i.test(value);
const isVideoUrl = (value) => /\.(mp4|webm|mov)(\?.*)?$/i.test(value);

const collectSectionContent = (value, fragments = []) => {
    if (!value) {
        return fragments;
    }

    if (typeof value === 'string') {
        if (isHtmlString(value)) {
            fragments.push({ type: 'html', value });
        }

        return fragments;
    }

    if (Array.isArray(value)) {
        value.forEach((item) => collectSectionContent(item, fragments));
        return fragments;
    }

    if (typeof value !== 'object') {
        return fragments;
    }

    CONTENT_KEYS.forEach((key) => {
        const item = value[key];

        if (typeof item === 'string' && item.trim()) {
            fragments.push({
                type: isHtmlString(item) ? 'html' : 'text',
                value: item,
            });
        }
    });

    MEDIA_URL_KEYS.forEach((key) => {
        const item = value[key];

        if (typeof item === 'string' && isVideoUrl(item)) {
            fragments.push({ type: 'video', value: item });
        } else if (typeof item === 'string' && isMediaUrl(item)) {
            fragments.push({ type: 'image', value: item });
        }
    });

    Object.keys(value).forEach((key) => {
        if (CONTENT_KEYS.includes(key) || MEDIA_URL_KEYS.includes(key) || key.endsWith('_id') || key.endsWith('_media')) {
            return;
        }

        collectSectionContent(value[key], fragments);
    });

    return fragments;
};

const getSectionContent = (section) => {
    const settings = parseSettings(section && section.settings_json);

    if (!settings) {
        return [];
    }

    return collectSectionContent(settings);
};

const scopeCmsCss = (css) => css.replace(/(^|})\s*([^@{}][^{]+)\{/g, (match, boundary, selectorText) => {
    const scopedSelectors = selectorText
        .split(',')
        .map((selector) => selector.trim())
        .filter(Boolean)
        .map((selector) => selector.startsWith('.cms-static-content') ? selector : `.cms-static-content ${selector}`)
        .join(', ');

    return `${boundary} ${scopedSelectors} {`;
});

const prepareCmsHtml = (html) => {
    if (!html) {
        return '';
    }

    return String(html)
        .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
        .replace(/<link\b[^>]*>/gi, '')
        .replace(/<meta\b[^>]*>/gi, '')
        .replace(/<style\b[^>]*>([\s\S]*?)<\/style>/gi, (match, css) => `<style>${scopeCmsCss(css)}</style>`);
};

class CmsStaticPage extends Component {
    constructor(props) {
        super(props);
        this.state = {
            loading: true,
            page: null,
            version: null,
            error: null,
        };
    }

    componentDidMount() {
        this.loadPage();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.slug !== this.props.slug) {
            this.loadPage();
        }
    }

    async loadPage() {
        this.setState({ loading: true, error: null });

        try {
            const response = await storefrontApi.getPage(this.props.slug);
            const data = response.data && response.data.data ? response.data.data : {};

            this.setState({
                loading: false,
                page: data.page || null,
                version: data.version || null,
            });
        } catch (error) {
            this.setState({
                loading: false,
                error: 'Page content is not available right now.',
            });
        }
    }

    renderSection(section) {
        const settings = parseSettings(section && section.settings_json) || {};
        const videoUrl = settings.video_url || (settings.video_media && settings.video_media.url);
        const posterUrl = settings.poster_url || (settings.poster_media && settings.poster_media.url);

        if ((section.section_type === 'video_banner' || section.type === 'video_banner') && videoUrl) {
            return (
                <section className="ps-storefront-section ps-storefront-video-banner" key={section.id || section.section_key || section.sort_order}>
                    <div className="ps-storefront-video-banner__frame">
                        <video className="ps-storefront-video-banner__video" src={videoUrl} poster={posterUrl || undefined} autoPlay muted loop playsInline preload="metadata" />
                    </div>
                </section>
            );
        }

        const content = getSectionContent(section);

        return (
            <section className="ps-section__content" key={section.id || section.section_key || section.sort_order}>
                {content.map((item, index) => {
                    if (item.type === 'image') {
                        return (
                            <div className="cms-static-content" key={`${item.type}-${index}`}>
                                <img src={item.value} alt="" className="cms-static-content__image" />
                            </div>
                        );
                    }

                    if (item.type === 'video') {
                        return <video className="cms-static-content__video" src={item.value} autoPlay muted loop playsInline preload="metadata" key={`${item.type}-${index}`} />;
                    }

                    if (item.type === 'text') {
                        return (
                            <p className="ps-section__desc cms-static-content" key={`${item.type}-${index}`}>
                                {item.value}
                            </p>
                        );
                    }

                    return (
                        <div
                            className="ps-section__desc cms-static-content"
                            dangerouslySetInnerHTML={{ __html: prepareCmsHtml(item.value) }}
                            key={`${item.type}-${index}`}
                        />
                    );
                })}
            </section>
        );
    }

    renderEmptyState() {
        return (
            <div className="cms-static-empty">
                <div className="cms-static-empty__mark">NW</div>
                <h1>Content is being updated</h1>
                <p>
                    This page is temporarily unavailable while our content team refreshes the information.
                    Please check back shortly.
                </p>
                <a href="/" className="cms-static-empty__action">Back to Home</a>
            </div>
        );
    }

    render() {
        const { loading, version, error } = this.state;
        const sections = version && Array.isArray(version.sections) ? version.sections : [];
        const renderableSections = sections.filter((section) => getSectionContent(section).length > 0);

        if (loading) {
            return <Loader />;
        }

        return (
            <div className="ps-about cms-static-page">
                <div className="ps-about__content">
                    <section className="ps-about__project">
                        <div className="container">
                            {error || renderableSections.length === 0 ? this.renderEmptyState() : null}
                            {renderableSections.map((section) => this.renderSection(section))}
                        </div>
                    </section>
                </div>
            </div>
        );
    }
}

export default CmsStaticPage;
